<template>
  <div class="section">
    <div class="container">
      <!-- Page Header -->
      <div class="mb-lg">
        <div class="columns is-centered">
          <div class="column is-one-third">
            <h1 class="has-text-centered is-size-4">Remittance Report</h1>
          </div>
        </div>
      </div>
      <!-- Filters Toolbar -->
      <div class="has-background-white rounded-lg pa-md mb-xxs">
        <div class="columns">
          <div class="column">
            <CreditSummaryFilters :applied-filters="filters" @filter="filter" />
          </div>
          <div class="column is-narrow">
            <b-button size="is-medium" class="is-fullwidth" icon-left="download" @click="showExportModal = true" :loading="exporting">Export</b-button>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div>
        <div class="mb-md">
          <b-table
            :data="results"
            :mobile-cards="false"
            :loading="loading"
            hoverable
            class="is-size-7">
            <template>
              <!-- Supplier -->
              <b-table-column
                v-if="context.isMerchant()"
                label="Supplier"
                header-class="nowrap"
                cell-class="nowrap"
                v-slot="props">
                {{ props.row.brand.name }}
              </b-table-column>
              <!-- Merchant -->
              <b-table-column
                v-if="context.isSupplier()"
                label="Merchant"
                header-class="nowrap"
                cell-class="nowrap"
                v-slot="props">
                {{ props.row.retailer.name }}
              </b-table-column>
              <b-table-column
                label="Invoice Total"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p>
                  <a href="#" class="has-text-info" @click.prevent="navigateToInvoices(props.row)">
                    <CurrencyLocale :value="parseFloat(props.row.due_amount)" :currency="localeCurrency"/>
                  </a>
                </p>
              </b-table-column>
              <b-table-column
                label="Credit Amount"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p v-if="props.row.credit_amount">
                  <CurrencyLocale :value="parseFloat(props.row.credit_amount)" :currency="currency"/>
                </p>
                <p v-else> -- </p>
              </b-table-column>
              <!-- Amount owed invoice due amt - credit credit amt -->
              <b-table-column
                label="Amount Due"
                header-class="nowrap"
                cell-class="nowrap"
                width="128"
                v-slot="props">
                <p v-if="props.row.credit_amount">
                  <CurrencyLocale :value="parseFloat(props.row.due_amount - props.row.credit_amount)" :currency="currency"/>
                </p>
                <p v-else>
                  <CurrencyLocale :value="parseFloat(props.row.due_amount)" :currency="currency"/>
                </p>
              </b-table-column>
            </template>
            <template #empty>
              <div class="py-xxl has-text-grey has-text-centered">
                {{ (loading) ? "Loading data..." : 'No credit memos found!' }}
              </div>
            </template>
          </b-table>
        </div>
        <b-pagination
          :total="total"
          :current.sync="currentPage"
          :rounded="true"
          :per-page="perPage"
          @change="paginate">
        </b-pagination>
      </div>
      <!-- Modal: Export  -->
      <b-modal
  has-modal-card
  scroll="keep"
  :active.sync="showExportModal"
  :can-cancel="['x']"
>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Export Report</p>
    </header>
    <section class="modal-card-body">
      <p class="has-text-grey mb-lg">
        Select a date range below to export the report for that period.
        The export will be delivered to your email and will only include
        records that fall within the selected dates.
      </p>
      <date-filter
        v-model="recorded_at"
        :filter="paymentFilterOptions.recordedAt"
        :filterInline="true"
        filterInlineLabel="Payment Recorded At (Optional)"
      />
    </section>
    <footer class="modal-card-foot" style="justify-content:space-between">
      <b-button
        @click="showExportModal = false"
        :loading="exporting"
        :disabled="exporting">
        Close
      </b-button>
      <b-button
        type="is-primary"
        @click="scheduleExport"
        :loading="exporting"
        :disabled="exporting">
        Export
      </b-button>
    </footer>
  </div>
</b-modal>

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Filterable from '@/mixins/Filterable'
import Exportable from '@/mixins/Exportable'
import CreditSummaryFilters from '../components/Credits/CreditSummaryFilters'
import DateFilter from '@/components/Filters/DateFilter'
import * as filterOptions from '../../Payments/filters'

export default {
  components: {
    CreditSummaryFilters,
    DateFilter
  },
  data () {
    return {
      key: 'credit-summary',
      alwaysAttach: {
        is_paid: 0
      },
      // fallback currency if locale preferences doesn't exist
      currency: {
        id: 1,
        name: 'United States Dollar',
        symbol: '$',
        unit: {
          id: 1,
          name: 'United States Dollar',
          standard_unit: 'USD',
          type: 'cost',
          system: null,
          symbol: '$'
        }
      },
      exporting: false,
      recorded_at: null,
      paymentFilterOptions: filterOptions
    }
  },
  computed: {
    ...mapGetters(['context', 'auth']),
    endpoint () {
      return this.context.route + '/invoices/summary/'
    },
    creditSummaryEndpoint () {
      return this.context.route + '/credits/summary/'
    },
    exportEndpoint () {
      return this.endpoint + 'export-email/'
    },
    localeCurrency () {
      return this.context?.localePreferences?.currency?.currency || this.currency
    }
  },
  methods: {
    reload () {
      this.load()
    },
    defaultFilters () {
      return {
        preset: 'custom',
        search: {},
        facets: {},
        timestamps: {
          invoiced_at: [
            this.$dates().subtract(1, 'month').startOf('day').unix(),
            null
          ]
        }
      }
    },
    load () {
      this.cacheSessionFilters()
      this.loading = true
      const creditsQuery = {
        params: { ...this.query.params, has_payment: 0 }
      }
      delete creditsQuery.params.is_paid
      // on initial load, invoiced_at may not be in query.params yet
      // so fall back to the filters.timestamps directly
      const invoicedAtGte = creditsQuery.params.invoiced_at_gte ||
    (this.filters?.timestamps?.invoiced_at?.[0]) || null
      const invoicedAtLte = creditsQuery.params.invoiced_at_lte ||
    (this.filters?.timestamps?.invoiced_at?.[1]) || null

      delete creditsQuery.params.invoiced_at_gte
      delete creditsQuery.params.invoiced_at_lte

      if (invoicedAtGte) creditsQuery.params.created_at_gte = invoicedAtGte
      if (invoicedAtLte) creditsQuery.params.created_at_lte = invoicedAtLte

      Promise.all([
        this.$http.get(this.endpoint, this.query), // invoices summary
        this.$http.get(this.creditSummaryEndpoint, creditsQuery) // credits summary
      ]).then(([invoicesResponse, creditsResponse]) => {
        const invoices = invoicesResponse.data.results
        const credits = creditsResponse.data.results

        // Build a lookup map from credits keyed by connection_id for O(1) access
        const creditsMap = credits.reduce((map, credit) => {
          map[credit.connection_id] = credit
          return map
        }, {})

        // Merge credit data into each invoice row where connection_id matches
        this.results = invoices.map(invoice => {
          const matchingCredit = creditsMap[invoice.connection_id]
          return matchingCredit
            ? {
                ...invoice,
                credit_memo_count: matchingCredit.credit_memo_count,
                credit_subtotal: matchingCredit.subtotal, // renamed to avoid collision
                adjustments: matchingCredit.adjustments,
                credit_amount: matchingCredit.credit_amount
              }
            : invoice
        })

        this.total = invoicesResponse.data.count
      }).finally(() => {
        this.loading = false
      })
    },
    navigateToInvoices (row) {
      const connectionName = this.context.isSupplier() ? row.retailer.name : row.brand.name
      const invoicedAt = this.filters.timestamps && this.filters.timestamps.invoiced_at
      const invoicesFilters = {
        preset: 'custom',
        search: {},
        facets: {
          connection_id: [{ label: connectionName, value: row.connection_id }],
          is_paid: [{ label: 'No', value: 0 }]
        },
        timestamps: invoicedAt ? { created_at: invoicedAt } : {}
      }
      sessionStorage.setItem('invoices', JSON.stringify({
        view: 'list',
        filters: invoicesFilters,
        orderBy: '-id',
        currentPage: 1,
        perPage: 12
      }))
      this.$router.push({ name: 'invoices.list' })
    },
    scheduleExport () {
      this.exporting = true
      const exportQuery = { params: { } }
      if (this.recorded_at && this.recorded_at[0]) {
        exportQuery.params.recorded_at_gte = this.recorded_at[0]
      }
      if (this.recorded_at && this.recorded_at[1]) {
        exportQuery.params.recorded_at_lte = this.recorded_at[1]
      }
      const endpoint = this.context.route + '/payments/remittance-report/'
      this.$http.get(endpoint, exportQuery).then(response => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Remittance report export scheduled! Please allow 5-10 minutes for delivery.' })
      }).finally(() => {
        this.exporting = false
      })
    }
  },
  mounted () {
    this.load()
  },
  mixins: [Filterable, Exportable]
}
</script>
